// src/contexts/AuthContext.jsx
import React, {
  createContext,
  useContext,
  useReducer,
  useEffect,
  useCallback,
  useRef,
} from 'react';
import { supabase } from '@/config/supabaseConfig';
import authService, { createProfileIfNotExists } from '@/services/authService';

// ==================== CONTEXTO ====================

const AuthContext = createContext(null);

// useAuth é definido APENAS aqui.
// O arquivo src/hooks/useAuth.jsx deve simplesmente re-exportar este hook
// para não duplicar lógica. Ex:
//   import { useAuth } from '@/contexts/AuthContext';
//   export { useAuth };
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth deve ser usado dentro de AuthProvider');
  }
  return context;
};

// ==================== REDUCER ====================

const AUTH_ACTIONS = {
  SET_LOADING: 'SET_LOADING',
  SET_ERROR: 'SET_ERROR',
  CLEAR_ERROR: 'CLEAR_ERROR',
  LOGIN_SUCCESS: 'LOGIN_SUCCESS',
  LOGOUT_SUCCESS: 'LOGOUT_SUCCESS',
  UPDATE_PROFILE: 'UPDATE_PROFILE',
  SET_PREFERENCES: 'SET_PREFERENCES',
};

const DEFAULT_PREFERENCES = {
  theme: 'light',
  notifications: { email: true, push: true, sms: false },
  language: 'pt-BR',
  timezone: 'America/Sao_Paulo',
};

const initialState = {
  user: null,
  session: null,
  userProfile: null,
  preferences: DEFAULT_PREFERENCES,
  isAuthenticated: false,
  // isLoading começa true: a sessão ainda não foi verificada.
  // Só vira false após getSession() resolver (com ou sem sessão).
  isLoading: true,
  error: null,
};

function authReducer(state, action) {
  switch (action.type) {
    case AUTH_ACTIONS.SET_LOADING:
      return { ...state, isLoading: action.payload };

    case AUTH_ACTIONS.SET_ERROR:
      // Força isLoading: false ao registrar erro — evita spinner infinito
      return { ...state, error: action.payload, isLoading: false };

    case AUTH_ACTIONS.CLEAR_ERROR:
      return { ...state, error: null };

    case AUTH_ACTIONS.LOGIN_SUCCESS:
      return {
        ...state,
        user: action.payload.user,
        session: action.payload.session,
        userProfile: action.payload.userProfile,
        preferences: {
          ...DEFAULT_PREFERENCES,
          timezone:
            action.payload.userProfile?.timezone || DEFAULT_PREFERENCES.timezone,
          language:
            action.payload.userProfile?.language || DEFAULT_PREFERENCES.language,
        },
        isAuthenticated: true,
        isLoading: false,
        error: null,
      };

    case AUTH_ACTIONS.LOGOUT_SUCCESS:
      // Preserva isLoading: false explicitamente
      return { ...initialState, isLoading: false };

    case AUTH_ACTIONS.UPDATE_PROFILE:
      return {
        ...state,
        userProfile: { ...state.userProfile, ...action.payload },
        isLoading: false,
      };

    case AUTH_ACTIONS.SET_PREFERENCES:
      return {
        ...state,
        preferences: { ...state.preferences, ...action.payload },
      };

    default:
      return state;
  }
}

// ==================== VALIDAÇÕES ====================

function isValidEmail(email) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

function validateRegistrationData(userData) {
  const errors = [];

  if (!userData.email) errors.push('Email é obrigatório');
  else if (!isValidEmail(userData.email)) errors.push('Email inválido');

  if (!userData.password) errors.push('Senha é obrigatória');
  else if (userData.password.length < 6)
    errors.push('Senha deve ter pelo menos 6 caracteres');

  if (!userData.name || userData.name.trim().length < 2)
    errors.push('Nome deve ter pelo menos 2 caracteres');

  if (
    !userData.userType ||
    !['psicologo', 'supervisor', 'instituicao', 'internacional'].includes(
      userData.userType
    )
  )
    errors.push('Tipo de usuário inválido');

  if (['psicologo', 'supervisor', 'internacional'].includes(userData.userType)) {
    if (!userData.crp || userData.crp.trim().length < 5)
      errors.push('CRP é obrigatório e deve ter pelo menos 5 caracteres');
  }

  if (userData.userType === 'instituicao') {
    if (!userData.cnpj || userData.cnpj.trim().length < 14)
      errors.push('CNPJ é obrigatório');
  }

  if (!userData.acceptTerms) errors.push('É necessário aceitar os termos de uso');

  return { isValid: errors.length === 0, errors };
}

// ==================== PROVIDER ====================

export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // FIX: flag para evitar dispatch após desmontagem
  const mountedRef = useRef(true);

  // FIX: flag para evitar double-run de handleSession (race condition
  // entre getSession() e onAuthStateChange no mesmo ciclo de montagem)
  const sessionResolvedRef = useRef(false);

  // FIX 2: flag para bloquear dispatch duplicado de LOGIN_SUCCESS caso
  // onAuthStateChange dispare durante o await loadProfile() do getSession()
  const authProcessedRef = useRef(false);

  // ---------- Helpers de dispatch (seguros contra desmontagem) ----------

  const safeDispatch = useCallback((action) => {
    if (mountedRef.current) dispatch(action);
  }, []);

  const setLoading = useCallback(
    (loading) => safeDispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: loading }),
    [safeDispatch]
  );

  const setError = useCallback(
    (error) => safeDispatch({ type: AUTH_ACTIONS.SET_ERROR, payload: error }),
    [safeDispatch]
  );

  const clearError = useCallback(
    () => safeDispatch({ type: AUTH_ACTIONS.CLEAR_ERROR }),
    [safeDispatch]
  );

  // ---------- Carrega perfil do banco ----------

  const loadProfile = useCallback(async (user) => {
    try {
      await createProfileIfNotExists(user);
      const result = await authService.getUserData(user.id);
      return result.success ? result.data : null;
    } catch {
      // Retorna null: perfil ausente não deve travar o fluxo de autenticação
      return null;
    }
  }, []);

  // ---------- Listener principal do Supabase ----------

  useEffect(() => {
    mountedRef.current = true;
    sessionResolvedRef.current = false;

    // ETAPA 1: resolve sessão existente imediatamente.
    // É OBRIGATÓRIO chamar getSession() explicitamente.
    // onAuthStateChange sozinho NÃO dispara para sessões já salvas no
    // localStorage na versão atual do @supabase/supabase-js — o que
    // causaria isLoading: true para sempre após refresh da página.
    supabase.auth.getSession().then(async ({ data }) => {
      sessionResolvedRef.current = true;

      if (!mountedRef.current) return;

      if (data.session?.user) {
        // FIX 2: marca ANTES do await para bloquear o onAuthStateChange
        // que pode disparar durante o loadProfile e causar dispatch duplo
        authProcessedRef.current = true;
        const userProfile = await loadProfile(data.session.user);
        safeDispatch({
          type: AUTH_ACTIONS.LOGIN_SUCCESS,
          payload: {
            user: data.session.user,
            session: data.session,
            userProfile,
          },
        });
      } else {
        safeDispatch({ type: AUTH_ACTIONS.LOGOUT_SUCCESS });
      }
    });

    // ETAPA 2: escuta mudanças futuras (login, logout, token refresh).
    // Ignora o disparo INITIAL que coincide com getSession() acima.
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      // Pula o evento duplicado que ocorre logo após getSession()
      if (!sessionResolvedRef.current) return;
      if (!mountedRef.current) return;

      // FIX 2: Supabase v2 emite INITIAL_SESSION exatamente uma vez logo
      // após o listener ser registrado — coincide com o getSession() já
      // resolvido acima. Filtrar evita o dispatch duplo de LOGIN_SUCCESS.
      if (event === 'INITIAL_SESSION') return;

      if (session?.user) {
        const userProfile = await loadProfile(session.user);
        safeDispatch({
          type: AUTH_ACTIONS.LOGIN_SUCCESS,
          payload: { user: session.user, session, userProfile },
        });
      } else {
        authProcessedRef.current = false;
        safeDispatch({ type: AUTH_ACTIONS.LOGOUT_SUCCESS });
      }
    });

    return () => {
      mountedRef.current = false;
      subscription.unsubscribe();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Sem deps: este efeito roda uma única vez na montagem do Provider

  // ==================== AÇÕES ====================

  const login = useCallback(async (email, password) => {
    try {
      safeDispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: true });
      safeDispatch({ type: AUTH_ACTIONS.CLEAR_ERROR });

      if (!email || !password) throw new Error('Email e senha são obrigatórios');
      if (!isValidEmail(email)) throw new Error('Formato de email inválido');

      const result = await authService.login(email, password);
      if (!result.success) throw new Error(result.error);

      // LOGIN_SUCCESS é disparado pelo onAuthStateChange automaticamente
      // após authService.login() — não é necessário fazer dispatch aqui
      return { success: true };
    } catch (error) {
      safeDispatch({ type: AUTH_ACTIONS.SET_ERROR, payload: error.message });
      return { success: false, error: error.message };
    }
  }, [safeDispatch]);

  const register = useCallback(async (userData) => {
    try {
      safeDispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: true });
      safeDispatch({ type: AUTH_ACTIONS.CLEAR_ERROR });

      const validation = validateRegistrationData(userData);
      if (!validation.isValid) throw new Error(validation.errors[0]);

      const result = await authService.register(userData.email, userData.password, {
        name: userData.name,
        fullName: userData.name,
        userType: userData.userType,
        crp: userData.crp || null,
        specialties: userData.specialties || [],
        location: userData.location || '',
        phone: userData.phone || '',
        cnpj: userData.cnpj || null,
        formation: userData.formation || '',
        acceptedTerms: userData.acceptTerms,
      });

      if (!result.success) throw new Error(result.error);

      // Se o Supabase exige confirmação de email, o onAuthStateChange NÃO
      // dispara LOGIN_SUCCESS — portanto paramos o loading manualmente aqui
      safeDispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false });
      return { success: true };
    } catch (error) {
      safeDispatch({ type: AUTH_ACTIONS.SET_ERROR, payload: error.message });
      return { success: false, error: error.message };
    }
  }, [safeDispatch]);

  const logout = useCallback(async () => {
    try {
      safeDispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: true });

      const result = await authService.logout();

      if (!result.success) throw new Error(result.error);

      // LOGOUT_SUCCESS é disparado pelo onAuthStateChange.
      // FIX: se o evento não disparar (ex: sessão já expirada), garante
      // o estado limpo manualmente
      safeDispatch({ type: AUTH_ACTIONS.LOGOUT_SUCCESS });
      return { success: true };
    } catch (error) {
      // FIX: sempre para o loading no catch para não travar a UI
      safeDispatch({ type: AUTH_ACTIONS.SET_ERROR, payload: error.message });
      return { success: false, error: error.message };
    }
  }, [safeDispatch]);

  const updateProfile = useCallback(async (profileUpdates) => {
    if (!profileUpdates || Object.keys(profileUpdates).length === 0) {
      return { success: true };
    }

    try {
      safeDispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: true });
      safeDispatch({ type: AUTH_ACTIONS.CLEAR_ERROR });

      const result = await authService.updateUserProfile(profileUpdates);
      if (!result.success) throw new Error(result.error);

      safeDispatch({ type: AUTH_ACTIONS.UPDATE_PROFILE, payload: result.data });
      return { success: true, data: result.data };
    } catch (error) {
      safeDispatch({ type: AUTH_ACTIONS.SET_ERROR, payload: error.message });
      return { success: false, error: error.message };
    }
  }, [safeDispatch]);

  const updatePreferences = useCallback(async (newPreferences) => {
    if (!newPreferences || Object.keys(newPreferences).length === 0) {
      return { success: true };
    }

    try {
      // Persiste apenas campos mapeáveis no banco
      const dbFields = {};
      if (newPreferences.timezone) dbFields.timezone = newPreferences.timezone;
      if (newPreferences.language) dbFields.language = newPreferences.language;

      if (Object.keys(dbFields).length > 0) {
        const result = await authService.updateUserProfile(dbFields);
        if (!result.success) throw new Error(result.error);
      }

      safeDispatch({ type: AUTH_ACTIONS.SET_PREFERENCES, payload: newPreferences });
      return { success: true };
    } catch (error) {
      safeDispatch({ type: AUTH_ACTIONS.SET_ERROR, payload: error.message });
      return { success: false, error: error.message };
    }
  }, [safeDispatch]);

  const requestPasswordReset = useCallback(async (email) => {
    try {
      safeDispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: true });
      safeDispatch({ type: AUTH_ACTIONS.CLEAR_ERROR });

      if (!isValidEmail(email)) throw new Error('Email inválido');

      const result = await authService.resetPassword(email);
      if (!result.success) throw new Error(result.error);

      safeDispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false });
      return { success: true };
    } catch (error) {
      safeDispatch({ type: AUTH_ACTIONS.SET_ERROR, payload: error.message });
      return { success: false, error: error.message };
    }
  }, [safeDispatch]);

  const resetPassword = useCallback(async (newPassword) => {
    try {
      safeDispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: true });
      safeDispatch({ type: AUTH_ACTIONS.CLEAR_ERROR });

      if (!newPassword || newPassword.length < 6)
        throw new Error('A senha deve ter pelo menos 6 caracteres');

      const result = await authService.updatePassword(newPassword);
      if (!result.success) throw new Error(result.error);

      safeDispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false });
      return { success: true };
    } catch (error) {
      safeDispatch({ type: AUTH_ACTIONS.SET_ERROR, payload: error.message });
      return { success: false, error: error.message };
    }
  }, [safeDispatch]);

  // ==================== PERMISSÕES ====================

  const hasPermission = useCallback(
    (permission) => {
      if (!state.userProfile || !state.isAuthenticated) return false;
      const { user_type } = state.userProfile;

      switch (permission) {
        case 'create_agenda':
          return ['psicologo', 'supervisor', 'internacional'].includes(user_type);
        case 'seek_supervision':
          return ['psicologo', 'internacional'].includes(user_type);
        case 'provide_supervision':
          return user_type === 'supervisor';
        case 'manage_institution':
          return user_type === 'instituicao';
        default:
          return false;
      }
    },
    [state.userProfile, state.isAuthenticated]
  );

  const isProfileComplete = useCallback(() => {
    if (!state.userProfile) return false;
    const requiredFields = ['name', 'user_type'];
    return requiredFields.every(
      (field) =>
        state.userProfile[field] &&
        String(state.userProfile[field]).trim() !== ''
    );
  }, [state.userProfile]);

  // ==================== CONTEXT VALUE ====================

  const contextValue = React.useMemo(
    () => ({
      user: state.user,
      session: state.session,
      userProfile: state.userProfile,
      preferences: state.preferences,
      isAuthenticated: state.isAuthenticated,
      isLoading: state.isLoading,
      error: state.error,
      login,
      register,
      logout,
      requestPasswordReset,
      resetPassword,
      updateProfile,
      updatePreferences,
      hasPermission,
      isProfileComplete,
      setError,
      clearError,
      setLoading,
    }),
    [
      state,
      login,
      register,
      logout,
      requestPasswordReset,
      resetPassword,
      updateProfile,
      updatePreferences,
      hasPermission,
      isProfileComplete,
      setError,
      clearError,
      setLoading,
    ]
  );

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

// ==================== HOOKS DERIVADOS ====================

export const usePermissions = () => {
  const { hasPermission, userProfile } = useAuth();
  return {
    canCreateAgenda: hasPermission('create_agenda'),
    canSeekSupervision: hasPermission('seek_supervision'),
    canProvideSupervision: hasPermission('provide_supervision'),
    canManageInstitution: hasPermission('manage_institution'),
    hasPermission,
    userType: userProfile?.user_type,
  };
};

export const useCurrentUser = () => {
  const { user, userProfile, isAuthenticated, isLoading } = useAuth();

  const displayName =
    userProfile?.name || user?.email?.split('@')[0] || 'Usuário';

  const initials = displayName
    .split(' ')
    .map((n) => n.charAt(0))
    .join('')
    .toUpperCase()
    .slice(0, 2);

  return {
    user,
    userProfile,
    isAuthenticated,
    isLoading,
    displayName,
    initials,
    email: user?.email,
    userType: userProfile?.user_type,
    isVerified: Boolean(user?.email_confirmed_at),
  };
};

export const useUserPreferences = () => {
  const { preferences, updatePreferences } = useAuth();

  const updateTheme = useCallback(
    (theme) => updatePreferences({ theme }),
    [updatePreferences]
  );

  const updateNotifications = useCallback(
    (notificationSettings) =>
      updatePreferences({
        notifications: {
          ...preferences.notifications,
          ...notificationSettings,
        },
      }),
    [updatePreferences, preferences.notifications]
  );

  return { preferences, updatePreferences, updateTheme, updateNotifications };
};

export default AuthContext;
